import { PayableRow } from '@bariendo/lib/payables';
import { useMedplum } from '@medplum/react';
import { useMemo } from 'react';
import { alert } from '@/services/alert';
import { fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { formatLedgerMoney } from '../ledger/ledgerRows';

export interface PayableActions {
  setActual: (row: PayableRow, actual: number) => Promise<void>;
  markPaid: (rows: PayableRow[], paid: { paidOn: string; reference: string }) => Promise<void>;
  /** Reverses a payment after the toast has gone (BAR-3125); Undo puts each line's date and reference back. */
  markUnpaid: (rows: PayableRow[]) => Promise<void>;
  saveNote: (row: PayableRow, note: string | null) => Promise<void>;
}

const message = (error: unknown) => (error instanceof Error ? error.message : 'unknown error');

/** Finance's actions on a payable (BAR-3046), each with the prototype's toast and Undo. */
export function usePayableActions(reload: () => void): PayableActions {
  const medplum = useMedplum();

  return useMemo(() => {
    // A failed write re-reads the lines: one that timed out may still have landed (BAR-3125).
    const call = <T>(input: Record<string, unknown>) =>
      fetchBotWrite<T>(medplum, 'create-treatment-payment', input, reload);
    const plural = (n: number) => `${n} ${n === 1 ? 'line' : 'lines'}`;

    return {
      setActual: async (row, actual) => {
        await call({ operation: 'setPayableActual', payableId: row.id, actualAmount: actual });
        reload();
        showUndoToast(`Actual set to ${formatLedgerMoney(actual)} for ${row.payeeName}`, () => {
          call({ operation: 'setPayableActual', payableId: row.id, actualAmount: row.actualAmount })
            .then(reload)
            .catch((error: unknown) => alert.error(`Couldn't undo the Actual: ${message(error)}`));
        });
      },

      markPaid: async (rows, paid) => {
        const payableIds = rows.map((row) => row.id);
        await call({
          operation: 'markPayablesPaid',
          payableIds,
          paidOn: paid.paidOn,
          paymentReference: paid.reference,
        });
        reload();
        showUndoToast(`${plural(rows.length)} marked paid · moved to Paid`, () => {
          call({ operation: 'undoPayablesPaid', payableIds })
            .then(reload)
            .catch((error: unknown) => alert.error(`Couldn't undo the payment: ${message(error)}`));
        });
      },

      markUnpaid: async (rows) => {
        await call({ operation: 'undoPayablesPaid', payableIds: rows.map((row) => row.id) });
        reload();
        // Lines paid together share a date and reference; re-mark each such group as it was.
        const byPayment = new Map<string, PayableRow[]>();
        for (const row of rows) {
          const key = JSON.stringify([row.paidOn, row.reference]);
          byPayment.set(key, [...(byPayment.get(key) ?? []), row]);
        }
        const remark = async (): Promise<void> => {
          for (const group of byPayment.values()) {
            await call({
              operation: 'markPayablesPaid',
              payableIds: group.map((row) => row.id),
              paidOn: group[0].paidOn,
              paymentReference: group[0].reference ?? '',
            });
          }
          reload();
        };
        showUndoToast(`${plural(rows.length)} marked unpaid · moved to Open`, () => {
          remark().catch((error: unknown) => alert.error(`Couldn't undo: ${message(error)}`));
        });
      },

      saveNote: async (row, note) => {
        await call({ operation: 'setPayableNote', payableId: row.id, note });
        reload();
      },
    };
  }, [medplum, reload]);
}
